/**
 * validate.js — Shared frontend validation helpers
 *
 * All functions follow the same convention:
 *   - Return true  → value is valid (or empty/optional and not provided)
 *   - Return false → value is present but invalid
 *
 * Empty/missing values always pass (use a separate `required` check for mandatory fields).
 */

import { formatBytes } from './format';

export const isValidEmail   = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
/* Leading "(" and a trailing extension both allowed. The old pattern demanded
   the first character be "+" or a digit, so the single most common written
   form of a local number — `(0803) 555-1234` — was rejected, as was anything
   carrying `ext 21`. That is a nuisance in a form and a wall in the grid,
   where one pasted column of real numbers would redden every cell and disable
   Save for the whole buffer. */
export const isValidPhone   = (v) => !v || /^(?=(?:\D*\d){6,})[+(\d][\d\s\-(). ]{5,}(?:\s*(?:x|ext\.?)\s*\d{1,6})?$/i.test(v);
export const isFutureDate   = (v) => !v || new Date(v) > new Date();
export const isPositiveNum  = (v) => v === '' || v === null || v === undefined || Number(v) > 0;
export const isNonNegative  = (v) => v === '' || v === null || v === undefined || Number(v) >= 0;
export const isAlphaNum     = (v) => !v || /^[a-zA-Z0-9\s-]+$/.test(v);
export const isValidYear    = (v) => !v || (Number(v) >= 1900 && Number(v) <= new Date().getFullYear() + 1);

/** Returns a unique toast id */
export const toastId = () => `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

/** Builds a success toast object */
export const successToast = (title, message) => ({ id: toastId(), severity: 'success', title, message });

/** Builds a danger (error) toast object */
export const errorToast = (title, message) => ({ id: toastId(), severity: 'danger', title, message });

/** Builds a warning toast object */
export const warningToast = (title, message) => ({ id: toastId(), severity: 'warning', title, message });

const FIELD_LABELS = {
  plateNumber: 'Plate number', vehicleName: 'Vehicle name', name: 'Name',
  driverName: 'Driver name', email: 'Email', phone: 'Phone',
  phoneNumber: 'Phone number', mobilePhoneNumber: 'Mobile phone',
  password: 'Password', username: 'Username', companyName: 'Organization name',
};

/**
 * Normalizes any caught API error into a safe, user-facing message.
 * Never exposes paths, stack traces, or raw HTTP status strings to the UI.
 * Logs full technical details to console for developers.
 *
 * @param {Error} err - The caught error (ideally an ApiError with .status/.body)
 * @param {string} action - Short verb phrase, e.g. 'save fleet', 'delete driver'
 * @param {{ context?: string }} options - Pass { context: 'login' } on the login page
 * @returns {{ message: string, fields: Object|null }}
 */
/* Thrown by the JS engine, not by us. Their text names variables and
   properties, which is useful in a console and meaningless in a toast. */
const ENGINE_ERROR_NAMES = new Set([
  'TypeError', 'ReferenceError', 'RangeError', 'SyntaxError', 'EvalError', 'URIError',
]);

export function normalizeApiError(err, action = 'complete this action', options = {}) {
  console.error('[API Error]', err);

  /* `status` MUST BE ABSENT, NOT ZERO, WHEN THE ERROR DIDN'T COME FROM THE API.
     `err?.status ?? 0` gave every non-ApiError a status of 0, and the branch
     below reads 0 as "the request never reached the server" — so anything
     thrown outside the fetch wrapper was reported to the user as a connection
     problem. That covers real messages the code went to the trouble of
     writing: `throw new Error('Start date must be before end date.')` in
     trackingReplay, and the server's own text on the change-password and
     update-status calls in fleetApi, which throw plain Errors rather than
     ApiError. The user saw "Service unavailable. Check your connection and try
     again." and went to check a connection that was fine. A JS bug in our own
     mapper reads the same way.
     ApiError always sets `status` (0 for a timeout), so its presence is what
     distinguishes "the API answered, or failed to" from "something else threw". */
  const isApiError = err != null && typeof err === 'object' && 'status' in err;
  const status = isApiError ? (err.status ?? 0) : null;
  const body   = err?.body ?? null;

  // Nothing to go on at all.
  if (err == null) return { message: 'An unexpected error occurred.', fields: null };

  // Network / timeout — only when this really was a request that didn't land.
  if (status === 0 || err?.name === 'AbortError') {
    return { message: 'Service unavailable. Check your connection and try again.', fields: null };
  }

  /* Not an API failure: something in our own code threw. A message we wrote
     deliberately — `throw new Error('Start date must be before end date.')` —
     was written for a person and should be shown verbatim rather than buried
     under an invented diagnosis.

     But a crash is not a message. The JS engine throws TypeError, ReferenceError
     and friends with text aimed at whoever is holding a debugger, so passing
     every thrown message straight through put "Cannot read properties of
     undefined (reading 'id')" in front of a dispatcher. The constructor name is
     what separates the two: code that means to talk to a user throws a plain
     Error; the engine never does. */
  if (!isApiError) {
    const own = typeof err.message === 'string' ? err.message.trim() : '';
    if (own && !ENGINE_ERROR_NAMES.has(err.name)) return { message: own, fields: null };
    return { message: `Could not ${action}. Please try again.`, fields: null };
  }

  if (status === 401) {
    return options?.context === 'login'
      ? { message: 'Invalid email or password.', fields: null }
      : { message: 'Your session has expired. Please sign in again.', fields: null };
  }
  // 402 — a plan limit was hit. Two different shapes arrive here:
  //   Vehicle/create   → { message, current, limit }
  //   Documents upload → { message, currentBytes, limitBytes, fileBytes }
  // Both normally carry `message`, which already names the numbers, so prefer
  // it verbatim and don't append a CTA on top of it. The fallbacks only cover a
  // bodyless/partial 402 — and must never say "vehicles" for a storage
  // rejection, which is exactly what the single hardcoded fallback used to do.
  if (status === 402) {
    if (body?.message) return { message: body.message, fields: null };

    if (body?.limitBytes != null) {
      return {
        message: `Your plan's storage limit (${formatBytes(body.limitBytes)}) has been reached. `
               + 'Delete some documents or upgrade your plan.',
        fields: null,
      };
    }

    const current = body?.current;
    const limit   = body?.limit;
    return {
      message: (current != null && limit != null)
        ? `Plan limit reached: ${current} of ${limit} vehicles. Upgrade your plan to add more.`
        : 'Your plan limit has been reached. Upgrade your plan to continue.',
      fields: null,
    };
  }
  /* 403 — a per-view Add/Edit/Delete flag blocked the write. The server names
     the action ("You don't have permission to edit on this page."), which is
     more useful than the generic line, so prefer it when present. */
  if (status === 403) {
    const said = typeof body === 'string' ? body : body?.message;
    return { message: (typeof said === 'string' && said.trim()) || "You don't have permission to do that.", fields: null };
  }
  if (status === 404) return { message: 'The requested item was not found.', fields: null };
  /* 409 — prefer what the server said. Zone/delete and Fleet/delete answer
     with `{ message: "Zone cannot be deleted: 2 fleet(s) are connected
     (North, South).", fleets: [...] }`, and the whole point of that message is
     the names. The fixed text stays as the fallback for a bodyless 409 (a
     duplicate-key conflict from an older endpoint). `typeof body === 'string'`
     because parseErrorBody hands back whatever JSON.parse produced, and some
     endpoints return a bare string — see engineCut.js. */
  if (status === 409) {
    const said = typeof body === 'string' ? body : body?.message;
    return { message: (typeof said === 'string' && said.trim()) || 'This record already exists or is still in use.', fields: null };
  }
  if (status >= 500)  return { message: 'A server problem occurred. Please try again shortly.', fields: null };

  if (body) {
    // ASP.NET model-state / validation errors object
    if (body.errors && typeof body.errors === 'object') {
      const fields = {};
      Object.entries(body.errors).forEach(([k, v]) => {
        const label = FIELD_LABELS[k] || k;
        fields[label] = Array.isArray(v) ? v : [String(v)];
      });
      const first = Object.values(fields).flat()[0];
      return { message: first || `Could not ${action}.`, fields };
    }
    const msg = body.message || body.title || body.detail;
    if (msg && typeof msg === 'string') return { message: msg, fields: null };
  }

  return { message: `Could not ${action}. Please try again.`, fields: null };
}

/** @deprecated Use normalizeApiError instead */
export function parseApiError(error) {
  return normalizeApiError(error).message;
}

/**
 * True for a 402 raised by the document store (the plan's storage quota)
 * rather than by a resource cap such as the vehicle limit.
 *
 * Matches on the request path, not the message text: both 402s land in the
 * same catch on any page that uploads a photo while creating a record, and
 * the wording is server-controlled and localisable.
 */
export const isStorageLimitError = (err) =>
  err?.status === 402 && String(err?.path || '').includes('/api/Documents');

/**
 * The records standing in the way of a delete, from a 409 body shaped
 * `{ message, fleets: [{id, name}] }` (Zone/delete) or
 * `{ message, vehicles: [{id, name, plateNumber}] }` (Fleet/delete).
 *
 * Returns `{ message, items }` for ConfirmModal's `error` prop, or null when
 * this is not a blocked-delete 409 — so a caller can do
 * `setDeleteError(blockedDeleteError(err) || { message: parseApiError(err) })`.
 *
 * A vehicle is named by plate, then name, then `#id` — the same order the
 * server uses in its own message, so the list and the sentence agree.
 * `$values` because the API's JSON is reference-preserving and can wrap a
 * list that way; see `toArr` in api/fleetApi.js.
 */
export function blockedDeleteError(err) {
  if (err?.status !== 409) return null;
  const body = err.body;
  if (!body || typeof body !== 'object') return null;
  const list = (v) => (Array.isArray(v) ? v : Array.isArray(v?.$values) ? v.$values : []);
  const fleets   = list(body.fleets).map(f => f?.name || `#${f?.id}`);
  const vehicles = list(body.vehicles).map(v => v?.plateNumber || v?.name || `#${v?.id}`);
  const items = fleets.length ? fleets : vehicles;
  const message = typeof body.message === 'string' && body.message.trim()
    ? body.message
    : normalizeApiError(err).message;
  return { message, items };
}
